import { Component, OnInit } from '@angular/core';
import {
  CatalogCardModel,
  CatalogMeModel,
  CatalogSectionModel,
  CatalogSummaryModel,
  CompositeProductInfoModel,
  CompositeProductModel,
  CompositeProductType,
  CountryModel,
  HxCatalogService,
  HxCountryService,
  HxProductInfoService,
  HxTagService,
  HxUiLabelPipe,
  PagedList,
  ProductLabel,
  UiLabel,
} from 'hx-services';
import { ActivatedRoute, ParamMap, Params, Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HxPagedListDirective } from 'hx-components';
import { provideTranslocoScope, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { NzPageHeaderComponent, NzPageHeaderExtraDirective, NzPageHeaderFooterDirective, NzPageHeaderSubtitleDirective, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzButtonComponent, NzButtonGroupComponent } from 'ng-zorro-antd/button';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import {
  NzFilterTriggerComponent,
  NzTableCellDirective,
  NzTableComponent,
  NzTbodyComponent,
  NzTdAddOnComponent,
  NzThAddOnComponent,
  NzTheadComponent,
  NzThSelectionComponent,
  NzThMeasureDirective,
  NzTrDirective
} from 'ng-zorro-antd/table';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';
import { NzDropdownMenuComponent } from 'ng-zorro-antd/dropdown';
import { NzCheckboxGroupComponent } from 'ng-zorro-antd/checkbox';
import { FormsModule } from '@angular/forms';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { CatalogMeService } from '../catalog/catalog-me.service';
import { CatalogNotifyService } from '../catalog/catalog-notify.service';
import { CatalogTabsComponent } from '../catalog/catalog-tabs.component';
import { labelText } from '../catalog/catalog-text';
import { AssignCard, AssignSectionData, AssignSectionModal } from './assign-section.modal';

/** A label of a card as the site paints it. */
interface LabelChip {
  code: string;
  text: string;
  color: string;
}

/** A row of the list; the cells are worked out here, the template only reads them. */
interface DataItem {
  id: number;
  title: UiLabel;
  countryId?: number;
  countryTitle: string;
  typeKey: string;
  price?: number;
  subProductTitles: string;
  labels: LabelChip[];
  sectionId?: number;
  sectionTitle: string;
  sectionDraft: boolean;
  /** the address of the page of the cake, empty until it is issued */
  slug: string;
  checked: boolean;
}

interface ListFilter {
  name: string;
  options: { label: string, value: string | number, checked?: boolean }[];
  isVisible: boolean;
  isActive: boolean;
  operator: 'and' | 'or';
}

/** The filter «Раздел»: everything, the cards in no section, the cards without an address, or one section (`section:<id>`). */
interface SectionFilter {
  selected: string;
  applied: string;
  isVisible: boolean;
  options: { value: string, label: string }[];
}

const NOBODY: CatalogMeModel = {admin: false, canEditCards: false, canCreateCards: false, cardRightsEnforced: false};
/** the catalog cannot filter by tags, so with a tag filter the cards of the section filter are read in whole (at most this many pages of 200) and cut here */
const MAX_CATALOG_PAGES = 5;

@Component({
  selector: 'hc-composite-products-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.less'],
  providers: [provideTranslocoScope('composite-products')],
  imports: [NzAffixComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzPageHeaderSubtitleDirective, NzPageHeaderExtraDirective, NzPageHeaderFooterDirective,
    NzButtonComponent, RouterLink, NzIconDirective, NzTableComponent, NzTheadComponent, NzTrDirective, NzTableCellDirective, NzThMeasureDirective, NzThAddOnComponent, NzThSelectionComponent, NzTdAddOnComponent, NzFilterTriggerComponent,
    NzTbodyComponent, NzButtonGroupComponent, NzWaveDirective, NzPopconfirmDirective, NzDropdownMenuComponent, NzCheckboxGroupComponent, FormsModule, NzRadioGroupComponent,
    NzRadioComponent, HxUiLabelPipe, TranslocoPipe, NzTagComponent, CatalogTabsComponent]
})
export class CompositeProductsListComponent extends HxPagedListDirective<DataItem> implements OnInit {
  countryFilter: ListFilter = {
    options: [], isActive: false, isVisible: false, operator: 'or', name: 'country_ids'
  };
  tagsFilter: ListFilter = {
    options: [], isActive: false, isVisible: false, operator: 'or', name: 'tags'
  };
  sectionFilter: SectionFilter = {selected: 'all', applied: 'all', isVisible: false, options: []};
  me: CatalogMeModel = NOBODY;
  summary?: CatalogSummaryModel;
  /** the cards ticked for «В раздел…», across the pages */
  selectedIds: number[] = [];
  allChecked = false;
  someChecked = false;

  private countries: CountryModel[] = [];
  private sections: CatalogSectionModel[] = [];
  private labels: ProductLabel[] = [];
  private filtersReady = false;
  /** the country the numbers under the header were asked for: they are asked again only when it changes or after an action, the query behind them reads the whole product stock */
  private summaryKey?: string;
  /** the title and country of every card the list has shown: the ticked cards of other pages are not loaded now, «В раздел…» still needs them */
  private shown = new Map<number, { title: UiLabel; countryId?: number }>();

  constructor(
    override router: Router,
    override aRoute: ActivatedRoute,
    private productInfoService: HxProductInfoService,
    private tr: TranslocoService,
    private tagService: HxTagService,
    private countryService: HxCountryService,
    private catalog: HxCatalogService,
    private meService: CatalogMeService,
    private notify: CatalogNotifyService,
    private modal: NzModalService,
  ) {
    super(router, aRoute);
  }

  ngOnInit() {
    void this.init();
  }

  resetFilter(filter: ListFilter) {
    filter.options.forEach(op => op.checked = false);
  }

  applyFilter(filter: ListFilter) {
    const checkedArr = filter.options.filter(v => v.checked).map(v => v.value);
    let filterParam: string | null = null;
    let filterOp: 'and' | 'or' | null = null;
    if (checkedArr.length !== 0) {
      filterParam = checkedArr.join(',');
      filterOp = filter.operator;
    }
    const queryParams: Params = {};
    queryParams[filter.name] = filterParam;
    queryParams[filter.name + '_op'] = filterOp;
    filter.isVisible = false;
    this.router.navigate([], {
      relativeTo: this.aRoute,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  resetSectionFilter() {
    this.sectionFilter.selected = 'all';
  }

  applySectionFilter() {
    const selected = this.sectionFilter.selected;
    this.sectionFilter.isVisible = false;
    this.router.navigate([], {
      relativeTo: this.aRoute,
      queryParams: {
        section_id: selected.startsWith('section:') ? selected.substring('section:'.length) : null,
        filter: selected === 'no_section' || selected === 'no_address' ? selected : null,
        page: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  /** the numbers under the table header work as filters too */
  showOnly(filter: 'no_section' | 'no_address') {
    this.sectionFilter.selected = filter;
    this.applySectionFilter();
  }

  check(item: DataItem, checked: boolean) {
    this.selectedIds = checked ? [...this.selectedIds.filter(id => id !== item.id), item.id] : this.selectedIds.filter(id => id !== item.id);
    this.list = this.list.map(row => row.id === item.id ? {...row, checked} : row);
    this.updateChecked();
  }

  checkAll(checked: boolean) {
    const pageIds = this.list.map(row => row.id);
    this.selectedIds = checked ? [...new Set([...this.selectedIds, ...pageIds])] : this.selectedIds.filter(id => !pageIds.includes(id));
    this.list = this.list.map(row => ({...row, checked}));
    this.updateChecked();
  }

  clearSelection() {
    this.selectedIds = [];
    this.list = this.list.map(row => ({...row, checked: false}));
    this.updateChecked();
  }

  /** «В раздел…» */
  openAssign() {
    const cards: AssignCard[] = this.selectedIds.map(id => {
      const known = this.shown.get(id);
      return {id, title: known ? labelText(this.tr.getActiveLang(), known.title) : `#${id}`, countryId: known?.countryId};
    });
    const data: AssignSectionData = {cards, sections: this.sections, countries: this.countries};
    this.modal.create({
      nzTitle: this.tr.translate('compositeProducts.assign.title'),
      nzContent: AssignSectionModal,
      nzData: data,
      nzWidth: 560,
      nzMaskClosable: false,
      nzFooter: null,
    }).afterClose.pipe(takeUntil(this.$destroyed)).subscribe(result => {
      if (result === 'success') {
        this.selectedIds = [];
        void this.reloadAll();
      }
    });
  }

  protected loadList(): Promise<PagedList<DataItem>> {
    return this.sectionFilter.applied === 'all' ? this.loadFromProducts() : this.loadFromCatalog();
  }

  remove(el: DataItem) {
    this.productInfoService.removeCompositeProduct(el.id)
      .subscribe(() => this.router.navigate([]));
  }

  /** the table is loaded by the base class; the state of the checkboxes of the header follows the rows that came */
  override async loadData() {
    await super.loadData();
    this.updateChecked();
  }

  private async init() {
    try {
      const [me, countries, sections, labels, tags] = await Promise.all([
        this.meService.me(),
        this.countryService.getCountries(),
        this.catalog.getSections().catch(() => [] as CatalogSectionModel[]),
        this.catalog.getProductLabels().then(set => set.labels).catch(() => [] as ProductLabel[]),
        this.tagService.getTags('CompositeProduct').catch(() => [] as string[]),
      ]);
      this.me = me;
      this.countries = countries;
      this.sections = sections;
      this.labels = labels;
      this.buildFilters(tags);
    } catch (err) {
      this.notify.error(err);
      return;
    }
    this.aRoute.queryParamMap.pipe(takeUntil(this.$destroyed))
      .subscribe(paramMap => {
        this.isTableLoading = true;
        this.syncFilters(paramMap);
        super.load(paramMap);
        void this.loadSummary(false);
      });
  }

  private buildFilters(tags: string[]) {
    const lang = this.tr.getActiveLang();
    this.countryFilter.options = this.countries.map(country => ({label: labelText(lang, country.title), value: country.id, checked: false}));
    // the labels of the dictionary first (with their names), then the tags the cards carry that the dictionary does not know
    const known = this.labels.map(label => ({label: labelText(lang, label.title) || label.code, value: label.code, checked: false}));
    const strange = tags.filter(tag => !this.labels.some(label => label.code === tag)).map(tag => ({label: tag, value: tag, checked: false}));
    this.tagsFilter.options = [...known, ...strange];
    this.sectionFilter.options = this.sections.map(section => {
      const country = this.countries.find(c => c.id === section.countryId);
      const draft = section.enabled ? '' : ` (${this.tr.translate('compositeProducts.sections.draft')})`;
      return {value: `section:${section.id}`, label: `${labelText(lang, section.title)}${draft} · ${labelText(lang, country?.title)}`};
    });
    this.filtersReady = true;
  }

  /** The address is the state of the filters: tick the boxes by it, so a link from another page and the browser's Back show what the table shows. */
  private syncFilters(paramMap: ParamMap) {
    if (!this.filtersReady) {
      return;
    }
    const countryIds = (paramMap.get('country_ids') ?? '').split(',').filter(Boolean).map(Number);
    const tags = (paramMap.get('tags') ?? '').split(',').filter(Boolean);
    this.countryFilter.options = this.countryFilter.options.map(op => ({...op, checked: countryIds.includes(Number(op.value))}));
    this.tagsFilter.options = this.tagsFilter.options.map(op => ({...op, checked: tags.includes(String(op.value))}));
    this.countryFilter.operator = paramMap.get('country_ids_op') === 'and' ? 'and' : 'or';
    this.tagsFilter.operator = paramMap.get('tags_op') === 'and' ? 'and' : 'or';
    this.countryFilter.isActive = this.countryFilter.options.some(op => op.checked);
    this.tagsFilter.isActive = this.tagsFilter.options.some(op => op.checked);
    const sectionId = paramMap.get('section_id');
    const filter = paramMap.get('filter');
    const applied = sectionId && this.sectionFilter.options.some(o => o.value === `section:${sectionId}`)
      ? `section:${sectionId}`
      : (filter === 'no_section' || filter === 'no_address' ? filter : 'all');
    this.sectionFilter.applied = applied;
    this.sectionFilter.selected = applied;
  }

  private checkedCountryIds(): number[] {
    return this.countryFilter.options.filter(op => op.checked).map(op => Number(op.value));
  }

  /** The list of the products as before; the column «Раздел» and the address come from the catalog for the cards of the page. */
  private async loadFromProducts(): Promise<PagedList<DataItem>> {
    const products = await firstValueFrom(this.productInfoService.getCompositeProducts({
      page: this.page,
      limit: this.limit,
      // language: this.language,
      sort: this.getSortParams(),
      countryIds: this.checkedCountryIds(),
      storesOp: this.countryFilter.operator,
      tags: this.tagsFilter.options.filter(op => op.checked).map(op => String(op.value)),
      tagsOp: this.tagsFilter.operator,
    }));
    const cards = await this.cardsOf(products.list.map(cp => cp.id));
    return {count: products.count, list: products.list.map(cp => this.itemOf(cp, cards.get(cp.id)))};
  }

  /** The list by the filter «Раздел»: the catalog gives the cards (in the order of the field «Порядок»), the product of each is read for the other columns. */
  private async loadFromCatalog(): Promise<PagedList<DataItem>> {
    const applied = this.sectionFilter.applied;
    const countryIds = this.checkedCountryIds();
    const query = {
      countryId: countryIds.length === 1 ? countryIds[0] : undefined,
      sectionId: applied.startsWith('section:') ? Number(applied.substring('section:'.length)) : undefined,
      filter: applied === 'no_section' || applied === 'no_address' ? applied : undefined,
    } as const;
    const tags = this.tagsFilter.options.filter(op => op.checked).map(op => String(op.value));
    let cards: CatalogCardModel[];
    let total: number;
    if (tags.length === 0) {
      const res = await this.catalog.getCards({...query, page: this.page, limit: this.limit});
      cards = res.list;
      total = res.count;
    } else {
      const all: CatalogCardModel[] = [];
      for (let page = 1; page <= MAX_CATALOG_PAGES; page++) {
        const res = await this.catalog.getCards({...query, page, limit: 200});
        all.push(...res.list);
        if (all.length >= res.count) {
          break;
        }
      }
      const matches = (card: CatalogCardModel) => this.tagsFilter.operator === 'and' ? tags.every(t => card.tags.includes(t)) : tags.some(t => card.tags.includes(t));
      const found = all.filter(matches);
      total = found.length;
      cards = found.slice((this.page - 1) * this.limit, this.page * this.limit);
    }
    const products = await Promise.all(cards.map(card => firstValueFrom(this.productInfoService.getCompositeProductById(card.compositeProductId)).catch(() => undefined)));
    return {count: total, list: cards.map((card, ix) => this.itemOf(products[ix] ?? card, card))};
  }

  private async cardsOf(ids: number[]): Promise<Map<number, CatalogCardModel>> {
    if (ids.length === 0) {
      return new Map();
    }
    try {
      const res = await this.catalog.getCards({ids, limit: 200});
      return new Map(res.list.map(card => [card.compositeProductId, card] as const));
    } catch {
      // the column «Раздел» is an addition: the list works without it
      return new Map();
    }
  }

  private itemOf(source: CompositeProductInfoModel | CompositeProductModel | CatalogCardModel, card?: CatalogCardModel): DataItem {
    const lang = this.tr.getActiveLang();
    const product = 'subProducts' in source ? source : undefined;
    // the list of products carries the country as an object, the catalog and the card as an id
    const countryId = source.countryId ?? ('country' in source ? source.country?.id : undefined);
    const section = card?.sectionId === undefined ? undefined : this.sections.find(s => s.id === card.sectionId);
    const id = 'compositeProductId' in source ? source.compositeProductId : source.id;
    this.shown.set(id, {title: source.title, countryId});
    const tags = source.tags ?? [];
    return {
      id,
      title: source.title,
      countryId,
      countryTitle: labelText(lang, this.countries.find(c => c.id === countryId)?.title),
      typeKey: product?.type ? `enum.CompositeProductType.${product.type as CompositeProductType}` : '',
      price: product?.price,
      subProductTitles: (product?.subProducts ?? []).map(sp => labelText(lang, sp.title)).join(', '),
      labels: tags.map(code => {
        const label = this.labels.find(l => l.code === code);
        return {code, text: label ? labelText(lang, label.title) || code : code, color: label?.color ?? ''};
      }),
      sectionId: card?.sectionId,
      sectionTitle: section ? labelText(lang, section.title) : '',
      sectionDraft: section ? !section.enabled : false,
      slug: card?.slug ?? '',
      checked: this.selectedIds.includes(id),
    };
  }

  private updateChecked() {
    const rows = this.list;
    const ticked = rows.filter(r => r.checked).length;
    this.allChecked = rows.length > 0 && ticked === rows.length;
    this.someChecked = ticked > 0 && ticked < rows.length;
  }

  private async loadSummary(force: boolean) {
    const countryIds = this.checkedCountryIds();
    const key = countryIds.length === 1 ? String(countryIds[0]) : 'all';
    if (!force && key === this.summaryKey) {
      return;
    }
    this.summaryKey = key;
    try {
      this.summary = await this.catalog.getSummary(countryIds.length === 1 ? countryIds[0] : undefined);
    } catch {
      this.summary = undefined;
      this.summaryKey = undefined;
    }
  }

  private async reloadAll() {
    await this.loadData();
    void this.loadSummary(true);
  }
}
